import { createFileRoute, Link } from "@tanstack/react-router";
import { MessageCircle, Calendar, Video, Camera, Users, TrendingUp, ArrowRight } from "lucide-react";
import { ServicePage } from "@/components/sections/service-page";
import { PageShell } from "@/components/site/page-shell";

export const Route = createFileRoute("/marketing/social-content/")({
  component: SocialContentIndex,
  head: () => ({
    meta: [
      { title: "Social & Content Marketing — CoderLity" },
      { name: "description", content: "Build a brand people actually care about with social strategy, content production and community management." },
      { property: "og:title", content: "Social & Content Marketing — CoderLity" },
      { property: "og:description", content: "Brand-building content and social strategy that earns attention, trust and customers." },
      { property: "og:url", content: "/marketing/social-content" },
    ],
    links: [{ rel: "canonical", href: "https://coderlity.com/marketing/social-content" }],
  }),
});

const social = [
  { slug: "social-strategy", title: "Social Strategy", desc: "Explore social strategy" },
  { slug: "community-management", title: "Community Management", desc: "Explore community management" },
  { slug: "influencer-marketing", title: "Influencer Marketing", desc: "Explore influencer marketing" },
];

const creative = [
  { slug: "video-production", title: "Video Production", desc: "Explore video production" },
  { slug: "graphic-design", title: "Graphic Design", desc: "Explore graphic design" },
  { slug: "brand-storytelling", title: "Brand Storytelling", desc: "Explore brand storytelling" },
];

function Group({ title, items }: { title: string; items: { slug: string; title: string; desc: string }[] }) {
  return (
    <section className="py-12">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <h2 className="text-3xl font-bold tracking-tight">{title}</h2>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((it) => (
            <Link
              key={it.slug}
              to="/marketing/social-content/$child"
              params={{ child: it.slug }}
              className="group rounded-2xl border border-border bg-card p-6 hover:border-primary/40 transition"
            >
              <h3 className="text-lg font-semibold">{it.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground inline-flex items-center gap-1">
                {it.desc} <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
              </p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function SocialContentIndex() {
  return (
    <>
      <ServicePage data={data} />
      <PageShell>
        <Group title="Social" items={social} />
        <Group title="Creative" items={creative} />
      </PageShell>
    </>
  );
}

const data = {
  eyebrow: "Marketing",
  title: "Social & Content",
  sub: "Build a brand people actually care about.",
  intro:
    "Most brands post into the void. We build social and content programs that earn real attention — through distinct voice, native-feeling creative, and a publishing rhythm that compounds. Whether you need a founder-led personal brand, a B2B thought-leadership engine, or a DTC content factory, we plan, produce, publish and report on every piece.",
  features: [
    { icon: Calendar, title: "Content calendar", description: "Monthly plans aligned to launches, seasons and trending topics." },
    { icon: Camera, title: "Photo + video shoots", description: "On-location or studio shoots with usage rights included." },
    { icon: Video, title: "Short-form video", description: "Reels, Shorts and TikToks edited for native algorithms." },
    { icon: MessageCircle, title: "Community management", description: "Reply, DM and engage on your behalf — in your voice." },
    { icon: Users, title: "Creator partnerships", description: "Sourced, briefed and managed influencer collaborations." },
    { icon: TrendingUp, title: "Trend monitoring", description: "Daily trend watch so you ride waves instead of chasing them." },
  ],
  process: [
    { step: "01", title: "Brand voice", description: "Voice doc, pillars and tone guardrails." },
    { step: "02", title: "Calendar", description: "Monthly content calendar with concepts and CTAs." },
    { step: "03", title: "Produce", description: "Photo, video and copy production with brand approval." },
    { step: "04", title: "Publish + report", description: "Native posting, community management, monthly insights." },
  ],
  deliverables: [
    "Brand voice and content pillars document",
    "Monthly content calendar (15–30 posts)",
    "Edited photo and video assets",
    "Native publishing across selected platforms",
    "Community management and engagement",
    "Monthly performance and insights report",
  ],
  techStack: ["Buffer", "Later", "Sprout Social", "Adobe Premiere", "CapCut", "Canva", "Notion"],
  pricing: [
    { name: "Single platform", price: "$2.5k", period: "month", features: ["1 platform", "15 posts / month", "Basic community mgmt", "Monthly report"] },
    { name: "Multi-platform", price: "$5k", period: "month", highlighted: true, features: ["3 platforms", "30 posts / month", "Full community mgmt", "Video editing"] },
    { name: "Full content engine", price: "$12k", period: "month", features: ["Photo + video shoots", "Creator partnerships", "Dedicated content team", "Strategy lead"] },
  ],
  faqs: [
    { q: "Which platforms should I be on?", a: "Whichever your customers spend time on. We start with audience research, then recommend a focused 1–3 platform strategy rather than spreading thin." },
    { q: "Do you do paid promotion?", a: "Organic and paid are different services — we handle both, and they work best when planned together." },
    { q: "Will you film original content?", a: "Yes — we run monthly shoots in our studio or on location, plus remote shoot kits for founder-led brands." },
    { q: "Who responds to comments and DMs?", a: "Our community managers, working from a response playbook approved by you. Sensitive conversations are escalated." },
  ],
};
